import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useDebounce } from '../../../../hooks/useDebounce';
import { Box, Grid, Typography, Button, Skeleton } from '@mui/material';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import useInternalBooking from '../../../../hooks/useInternalBooking';
import { useBooking } from '../../../../context/bookingContext';
import { useAuth } from '../../../../context/authContext';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

function TimeSelect({ selectedDate, onTimeSelect }) {
  const [startTimes, setStartTimes] = useState([]);
  const [isLoadingStartTimes, setIsLoadingStartTimes] = useState(false);
  const [fetchedStartTimesState, setFetchedStartTimesState] = useState(null);
  const auth = useAuth();
  const activeRequestId = useRef(0);
  const lastRequestedState = useRef(null);
  const lastFetchState = useRef({
    date: null,
    duration: null,
    totalPersons: 0,
    overbooking: null,
    selectedProduct: null,
    selectedResource: null,
    fetched: false,
  });
  const {
    selectedEntertainment,
    selectedProduct,
    selectedResource,
    selectedTime,
    setSelectedTime,
    selectedDuration,
    selectedCategories,
    previousSelectedDate,
    selectedPricingId,
    setSelectedPricingId,
    overbooking,
    shouldWaitForResourceSelection,
  } = useBooking();

  const { getStartTimes } = useInternalBooking();

  const fetchStartTimes = useCallback(async () => {
    const resetActiveState = () => {
      lastRequestedState.current = null;
      activeRequestId.current += 1;
      setIsLoadingStartTimes(false);
    };

    if (
      !selectedDate ||
      !dayjs.isDayjs(selectedDate) ||
      !selectedDate.isValid() ||
      !selectedDuration ||
      selectedCategories.length === 0
    ) {
      setStartTimes([]);
      setFetchedStartTimesState(null);
      lastFetchState.current.fetched = false;
      if (!previousSelectedDate || !selectedDate.isSame(previousSelectedDate, 'day')) {
        setSelectedTime(null);
        setSelectedPricingId(null);
      }
      resetActiveState();
      return;
    }

    // If product has resources and none is selected yet, don't make the call
    if (shouldWaitForResourceSelection(selectedProduct)) {
      setStartTimes([]);
      setFetchedStartTimesState(null);
      if (selectedTime !== null) {
        setSelectedTime(null);
      }
      if (selectedPricingId !== null) {
        setSelectedPricingId(null);
      }
      lastFetchState.current.fetched = false;
      resetActiveState();
      return;
    }

    const currentDate = selectedDate.format('YYYY-MM-DD');
    const currentTotalPersons = selectedCategories.reduce(
      (sum, cat) => sum + parseInt(cat.quantity || 0),
      0
    );

    const currentState = {
      date: currentDate,
      duration: selectedDuration,
      totalPersons: currentTotalPersons,
      overbooking,
      selectedProduct,
      selectedResource,
    };

    const matchesState = (state, candidate) => {
      if (!state) {
        return false;
      }
      return (
        state.date === candidate.date &&
        state.duration === candidate.duration &&
        state.totalPersons === candidate.totalPersons &&
        state.overbooking === candidate.overbooking &&
        state.selectedProduct === candidate.selectedProduct &&
        state.selectedResource === candidate.selectedResource
      );
    };

    const shouldFetch =
      !matchesState(lastFetchState.current, currentState) &&
      !matchesState(lastRequestedState.current, currentState);

    if (!shouldFetch) {
      return;
    }

    const requestId = activeRequestId.current + 1;
    activeRequestId.current = requestId;
    lastRequestedState.current = currentState;
    setIsLoadingStartTimes(true);
    try {
      const formattedCategories = selectedCategories.map(category => ({
        name: category.name || 'Personen',
        count: parseInt(category.quantity) || 0,
      }));

      const data = await getStartTimes(
        selectedEntertainment,
        selectedProduct,
        selectedDate.year(),
        selectedDate.month(),
        selectedDate.date(),
        formattedCategories,
        selectedDuration,
        overbooking,
        selectedResource
      );

      if (activeRequestId.current !== requestId) {
        return;
      }

      // Update lastFetchState immediately after successful API call
      lastFetchState.current = {
        date: currentDate,
        duration: selectedDuration,
        totalPersons: currentTotalPersons,
        overbooking,
        selectedProduct,
        selectedResource,
        fetched: true,
      };

      if (data?.availableStartTimes) {
        setStartTimes(data.availableStartTimes);
        setFetchedStartTimesState(currentState);
        if (selectedTime) {
          const isTimeStillAvailable = data.availableStartTimes.some(
            time => time.startTime === selectedTime
          );
          if (!isTimeStillAvailable) {
            setSelectedTime(null);
            setSelectedPricingId(null);
          }
        }
      } else {
        setStartTimes([]);
        setFetchedStartTimesState(null);
        if (!previousSelectedDate || !selectedDate.isSame(previousSelectedDate, 'day')) {
          setSelectedTime(null);
          setSelectedPricingId(null);
        }
      }
      lastRequestedState.current = null;
    } catch (error) {
      console.error('Failed to fetch start times:', error);
      if (activeRequestId.current === requestId) {
        toast.error('Fehler beim Laden der verfügbaren Zeiten.');
        setStartTimes([]);
        setFetchedStartTimesState(null);
        if (!previousSelectedDate || !selectedDate.isSame(previousSelectedDate, 'day')) {
          setSelectedTime(null);
          setSelectedPricingId(null);
        }
        lastFetchState.current.fetched = false;
        lastRequestedState.current = null;
      }
    } finally {
      if (activeRequestId.current === requestId) {
        setIsLoadingStartTimes(false);
      }
    }
  }, [
    selectedDate,
    selectedDuration,
    selectedCategories,
    selectedEntertainment,
    selectedProduct,
    selectedResource,
    overbooking,
    getStartTimes,
    previousSelectedDate,
    selectedTime,
    selectedPricingId,
    setSelectedTime,
    setSelectedPricingId,
    shouldWaitForResourceSelection,
  ]);

  const debouncedFetchStartTimes = useDebounce(fetchStartTimes, 150);

  useEffect(() => {
    debouncedFetchStartTimes();
  }, [
    selectedDate,
    selectedDuration,
    selectedResource,
    previousSelectedDate,
    overbooking,
    debouncedFetchStartTimes,
    shouldWaitForResourceSelection,
  ]);

  useEffect(() => {
    const currentTotalPersons = selectedCategories.reduce(
      (sum, cat) => sum + parseInt(cat.quantity || 0),
      0
    );
    if (currentTotalPersons !== lastFetchState.current.totalPersons) {
      debouncedFetchStartTimes();
    }
  }, [selectedCategories, debouncedFetchStartTimes]);

  const handleTimeSelect = time => {
    const selectedTimeData = startTimes.find(t => t.startTime === time);
    if (selectedTimeData) {
      setSelectedTime(time);
      setSelectedPricingId(selectedTimeData.pricingId);
      onTimeSelect(time);
    }
  };

  const currentTotalPersons = useMemo(() => {
    return selectedCategories.reduce((sum, cat) => sum + parseInt(cat.quantity || 0), 0);
  }, [selectedCategories]);

  // Split-Hinweis vorübergehend deaktiviert: Die Berechnung der Split-Infos
  // wird nicht mehr benötigt, da die zugehörige blaue Info-Box ausgeblendet
  // ist (Untersuchung, ob der Hinweis schlechte Golf-Buchungszahlen verursacht).
  /*
  const selectedTimeSplitInfo = useMemo(() => {
    const selectedTimeData = startTimes.find(time => {
      if (time.startTime !== selectedTime) {
        return false;
      }
      if (selectedPricingId) {
        return time.pricingId === selectedPricingId;
      }
      return true;
    });

    const groupCount = Number(selectedTimeData?.orderedResourcesGroupCount || 0);
    const participantAllocations = Array.isArray(selectedTimeData?.orderedResourcesParticipantAllocations)
      ? selectedTimeData.orderedResourcesParticipantAllocations
          .map(value => Number(value || 0))
          .filter(value => Number.isFinite(value) && value > 0)
      : [];

    return {
      hasSplit: Boolean(selectedTimeData?.orderedResourcesHasSequentialSplit) && groupCount > 1,
      groupCount,
      participantAllocations,
    };
  }, [selectedPricingId, selectedTime, startTimes]);
  */

  const isStartTimesFresh = useMemo(() => {
    if (!fetchedStartTimesState) return true;
    if (!selectedDate || !dayjs.isDayjs(selectedDate) || !selectedDate.isValid()) return true;
    const currentDate = selectedDate.format('YYYY-MM-DD');
    return (
      fetchedStartTimesState.date === currentDate &&
      fetchedStartTimesState.duration === selectedDuration &&
      fetchedStartTimesState.totalPersons === currentTotalPersons &&
      fetchedStartTimesState.overbooking === overbooking &&
      fetchedStartTimesState.selectedProduct === selectedProduct &&
      fetchedStartTimesState.selectedResource === selectedResource
    );
  }, [
    fetchedStartTimesState,
    selectedDate,
    selectedDuration,
    currentTotalPersons,
    overbooking,
    selectedProduct,
    selectedResource,
  ]);

  const showPricePerPerson = auth?.preferences?.startTimePricePerPerson !== false;

  const formatPerPersonValue = value => {
    if (value == null || value === '') return '';
    const numeric = typeof value === 'number' ? value : parseFloat(String(value).replace(',', '.'));
    if (!Number.isFinite(numeric)) return '';
    return numeric.toFixed(2).replace('.', ',') + '€ p.P.';
  };

  const formatPerPersonFromTotal = totalPrice => {
    if (currentTotalPersons <= 0 || totalPrice == null || totalPrice === '') return '';
    const numericTotal =
      typeof totalPrice === 'number'
        ? totalPrice
        : parseFloat(String(totalPrice).replace(',', '.'));
    if (!Number.isFinite(numericTotal)) return '';
    const perPerson = numericTotal / currentTotalPersons;
    if (!Number.isFinite(perPerson)) return '';
    return perPerson.toFixed(2).replace('.', ',') + '€ p.P.';
  };

  const formatTotalPrice = totalPrice => {
    if (totalPrice == null || totalPrice === '') return '';
    const numericTotal =
      typeof totalPrice === 'number'
        ? totalPrice
        : parseFloat(String(totalPrice).replace(',', '.'));
    if (!Number.isFinite(numericTotal)) return '';
    return numericTotal.toFixed(2).replace('.', ',') + '€';
  };

  if (selectedCategories.length === 0 || selectedDuration === null) {
    return null;
  }

  if (isLoadingStartTimes) {
    return (
      <>
        <Typography variant='body1' gutterBottom>
          Startzeit
        </Typography>
        <Grid container spacing={1} columns={{ xs: 3, sm: 5, md: 8, lg: 10, xl: 12 }}>
          {Array.from(new Array(12)).map((_, index) => (
            <Grid item xs={1} key={index}>
              <Skeleton
                variant='rectangular'
                sx={{
                  borderRadius: '20px',
                  height: '42px',
                  '&::after': {
                    animationDuration: '2.5s',
                  },
                }}
              />
            </Grid>
          ))}
        </Grid>
      </>
    );
  }

  if (startTimes.length > 0) {
    return (
      <>
        <Typography variant='body1' gutterBottom>
          Startzeit
        </Typography>
        <Grid container spacing={1} columns={{ xs: 3, sm: 5, md: 8, lg: 10, xl: 12 }}>
          {(() => {
            const anyOverbooked = startTimes.some(t => t.overbookedBy > 0);
            return startTimes.map(time => (
              <Grid item xs={1} key={time.startTime}>
                <Button
                  variant={selectedTime === time.startTime ? 'contained' : 'outlined'}
                  onClick={() => handleTimeSelect(time.startTime)}
                  sx={{
                    backgroundImage: time.requiresEmployeeConfirmation
                      ? 'repeating-linear-gradient(135deg, rgba(33,150,243,0.16) 0, rgba(33,150,243,0.16) 10px, rgba(255,255,255,0) 10px, rgba(255,255,255,0) 20px)'
                      : 'none',
                    color: selectedTime === time.startTime ? '#3b3a39' : 'text.primary',
                    borderColor: '#afaeae',
                    backgroundColor: selectedTime === time.startTime ? 'grey.200' : 'transparent',
                    borderRadius: '20px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.25,
                    py: 0.5,
                    minWidth: { xs: '50px', sm: '60px' },
                    width: '100%',
                    '&:hover': {
                      backgroundColor: 'grey.400',

                      borderColor: '#afaeae',
                    },
                  }}
                >
                  <Typography variant='body2' sx={{ lineHeight: 1 }}>
                    {time.startTime}
                  </Typography>
                  <Typography
                    variant='caption'
                    sx={{
                      fontSize: '0.6rem',
                      lineHeight: 1,
                      color:
                        selectedTime === time.startTime
                          ? '#3b3a39'
                          : time.priceColor || 'text.secondary',
                      opacity: selectedTime === time.startTime ? 1 : 0.8,
                    }}
                  >
                    {!isStartTimesFresh
                      ? '…'
                      : showPricePerPerson
                        ? time.pricePerPerson != null
                          ? formatPerPersonValue(time.pricePerPerson)
                          : formatPerPersonFromTotal(time.totalPrice)
                        : formatTotalPrice(time.totalPrice)}
                  </Typography>
                  {time.requiresEmployeeConfirmation ? (
                    <Typography
                      variant='caption'
                      sx={{
                        fontSize: '0.55rem',
                        lineHeight: 1,
                        color: 'info.main',
                        opacity: selectedTime === time.startTime ? 1 : 0.9,
                      }}
                    >
                      Auf Anfrage
                    </Typography>
                  ) : null}
                  {anyOverbooked &&
                    (time.overbookedBy > 0 ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <GroupAddIcon sx={{ fontSize: '0.8rem', color: 'error.main' }} />
                        <Typography
                          variant='caption'
                          sx={{
                            fontSize: '0.6rem',
                            lineHeight: 1,
                            color: 'error.main',
                            fontWeight: 'bold',
                          }}
                        >
                          +{time.overbookedBy}
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CheckCircleIcon sx={{ fontSize: '0.8rem', color: 'success.main' }} />
                      </Box>
                    ))}
                </Button>
              </Grid>
            ));
          })()}
        </Grid>
        {/*
          Split-Hinweis vorübergehend komplett deaktiviert (auskommentiert).
          Diese blaue Info-Box hat dem Nutzer angezeigt, dass die Auswahl auf
          mehrere direkt aufeinanderfolgende Slots aufgeteilt wird. Wir blenden
          den Hinweis aus, um zu untersuchen, ob er fuer schlechte
          Golf-Buchungszahlen verantwortlich ist.
        */}
        {/* selectedTimeSplitInfo.hasSplit ? (
          <Alert severity='info' sx={{ mt: 2 }}>
            Diese Auswahl wird mit dieser Personenanzahl auf{' '}
            <strong>
              {selectedTimeSplitInfo.groupCount === 2
                ? 'zwei direkt aufeinanderfolgende Slots'
                : `${selectedTimeSplitInfo.groupCount} direkt aufeinanderfolgende Slots`}
            </strong>{' '}
            aufgeteilt.
            {selectedTimeSplitInfo.participantAllocations.length > 1 ? (
              <>
                {' '}
                Voraussichtliche Aufteilung:{' '}
                <strong>{selectedTimeSplitInfo.participantAllocations.join(' + ')} Personen</strong>.
              </>
            ) : null}
          </Alert>
        ) : null */}
      </>
    );
  }

  if (startTimes.length === 0) {
    return (
      <Typography variant='body1' gutterBottom>
        Keine Startzeiten verfügbar
      </Typography>
    );
  }

  return null;
}

export default TimeSelect;
